import React, {useEffect, useState} from 'react';
import {  EyeIcon } from 'lucide-react';
import {Link, useParams} from "react-router-dom";
import useApi from "../../../utils/useApi";
import LoadingScreen from "../../LoadingScreen/LoadingScreen";
import {EnvelopeIcon} from "@heroicons/react/16/solid";
import emailTemplateIcon from "../../../assets/emails-template.svg";

const EmailTemplateSelector = ( {handleShowHeader, handleShowSettingsHeader}) => {
    const { uid } = useParams();
    const { getEmailTemplates } = useApi();
    const [client, setClient] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [templates, setTemplates] = useState([]);
    const [filteredTemplates, setFilteredTemplates] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        handleShowHeader(true);
        handleShowSettingsHeader(false);
        initialize();
    }, [uid]);

    const initialize = async () => {
        try {
            setLoading(true);
            const response = await getEmailTemplates(uid);
            setTemplates(response.templates);
            setFilteredTemplates(response.templates);
            setClient(response.client);
        } catch (error) {
            console.error('Error fetching templates:', error);
            setTemplates([]);
            setFilteredTemplates([]);
        } finally {
            setLoading(false);
        }
    };

    if(loading){
        return <LoadingScreen />;
    }

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 page-container relative z-10">
            <div className="p-4 md:p-6">
                {/* Header */}
                <header className="mb-6">
                    <div className="container mx-auto">
                        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                            <div>
                                <h3 className="text-2xl font-semibold text-gray-800 dark:text-gray-200">
                                    Select Template:
                                    <small className="ml-2 text-gray-600 dark:text-gray-400">
                                        <Link to={`/client/${client.id}/edit`} className="hover:text-blue-600 dark:hover:text-blue-400">
                                            {client.profile?.client}
                                        </Link>
                                    </small>
                                </h3>
                            </div>

                            <div className="flex flex-row gap-3 w-full md:w-auto">
                                <Link
                                    to={`/view-letters/${client.id}`}
                                    className="inline-flex items-center justify-center gap-2 px-4 py-2
                    min-w-[140px] md:min-w-0
                    text-xs
                    bg-blue-500 dark:bg-blue-600
                    text-white
                    hover:bg-blue-600 dark:hover:bg-blue-700
                    rounded-md transition-colors"
                                >
                                    View Sent Letters
                                    <EnvelopeIcon className="ml-2 h-4 w-4" />
                                </Link>

                                <Link
                                    to={`/documents/${client.id}`}
                                    className="inline-flex items-center justify-center px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors
                    min-w-[140px] md:min-w-0
                    text-xs
                    text-white
                    rounded-md transition-colors"
                                >
                                    Documents
                                    <EyeIcon className="ml-2 h-4 w-4" />
                                </Link>
                            </div>
                        </div>
                    </div>
                </header>

                {/* Main Content */}
                <div className="container mx-auto">
                    <div className="w-full">
                        <section className="animate-fadeIn">

                            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md">
                                <div className="border-b border-gray-200 dark:border-gray-700 px-6 py-4">
                                    <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-200">
                                        Select Email Template
                                    </h2>
                                </div>

                                <div className="p-6 md:p-8">
                                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                                        {templates && templates.length > 0 ? (
                                            templates.map((template) => (
                                                <div
                                                    key={template.id}
                                                    className="group hover:transform hover:scale-105 transition-all duration-200"
                                                >
                                                    <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                                                        <Link to={`/email-template/${template.id}/${client.id}`}>
                                                            <img
                                                                src={emailTemplateIcon}
                                                                alt={template.title}
                                                                className="w-full h-auto object-cover mb-4 rounded border-2 border-transparent group-hover:border-blue-500 dark:group-hover:border-blue-400 transition-colors"
                                                            />
                                                            <h5 className="font-medium text-gray-800 dark:text-gray-200 group-hover:text-blue-500 dark:group-hover:text-blue-400">
                                                                {template.title}
                                                            </h5>
                                                        </Link>
                                                    </div>
                                                </div>
                                            ))
                                        ) : (
                                             <div className="col-span-full">
                                                 <p className="text-red-500 dark:text-red-400 p-4 bg-red-100 dark:bg-red-900/50 rounded-lg">
                                                     No template found.
                                                 </p>
                                             </div>
                                         )}
                                    </div>
                                </div>
                            </div>
                        </section>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EmailTemplateSelector;
